import Card from "components/card";
import axiosClient from "axios-client";
import Divider from "components/Divider";
import { useEffect, useState } from "react";
import Paginator from "components/Paginator";
import { useNavigate } from "react-router-dom";
import Loading from "components/loading/Loading";
import { ACTIVATION_TYPES } from "utils/constants";
import ErrorMessage from "components/ErrorMessage";
import BasicLayout from "components/wizard/BasicLayout";
import { useStateContext } from "context/ContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActivationBadgeType from "components/ActivationTypeBadge";
import {
    faKeySkeleton,
    faPen,
    faPowerOff,
} from "@fortawesome/pro-regular-svg-icons";

const NewActivation = () => {
    const navigate = useNavigate();
    const { user, searchValue } = useStateContext();

    const [load, setLoad] = useState(false);
    const [message, setMessage] = useState({});
    const [resellerOrders, setResellerOrders] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [resellersOrdersPagination, setResellersOrdersPagination] =
        useState();

    const totalPages = Math.ceil(
        resellersOrdersPagination?.total / resellersOrdersPagination?.per_page
    );

    useEffect(() => {
        setLoad(true);

        axiosClient
            .get("/reseller-order/list", {
                params: {
                    filter: searchValue,
                    page: currentPage,
                    per_page: 18,
                },
            })
            .then(({ data }) => {
                setResellerOrders(data.data.data);
                setResellersOrdersPagination(data.data);
            })
            .catch((err) => {
                const response = err.response;

                if (response) {
                    setMessage({
                        text: response.data.message,
                        type: "ERROR",
                        errors: response.data.errors
                            ? response.data.errors
                            : {},
                    });
                }
            })
            .finally(() => {
                setLoad(false);
            });
    }, [searchValue, currentPage]);

    const activationTypes = [
        {
            id: 1,
            action: selectType,
            actionParam: ACTIVATION_TYPES.ONSITE,
            src: "/img/proxima-onsite.svg",
            alt: "Proxima Onsite",
        },
        {
            id: 2,
            action: selectType,
            actionParam: ACTIVATION_TYPES.CLOUD,
            src: "/img/proxima-cloud.svg",
            alt: "Proxima Cloud",
        },
        {
            id: 3,
            action: openInNewTab,
            actionParam: "https://docs-api.myproxima.info/carrier/v1/",
            src: "/img/proxima-carrier.svg",
            alt: "Proxima Caarrier",
        },
    ];

    function openInNewTab(url) {
        const newWindow = window.open(url, "_blank", "noopener,noreferrer");
        if (newWindow) newWindow.opener = null;
    }

    function selectType(type) {
        if (type !== null) {
            setLoad(true);
            navigate(`/my/activation-${type}`);
        }
    }

    return (
        <>
            {load && <Loading />}

            <Card extra="px-4 md:px-6 py-2 md:py-5 w-full h-full sm:overflow-x-auto gap-8">
                {user?.allow_new_user_requests && (
                    <BasicLayout
                        title="Crea nuova attivazione"
                        subtitle="Scegli la tipologia di prodotto che vuoi attivare."
                        content={
                            <>
                                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                                    {activationTypes.map(
                                        ({
                                            id,
                                            action,
                                            actionParam,
                                            src,
                                            alt,
                                        }) => (
                                            <div
                                                key={id}
                                                className={`flex cursor-pointer items-center justify-center rounded-3xl bg-white py-6 
                                                px-4 shadow-lg shadow-shadow-500 transition-all duration-300 hover:shadow-lg dark:!bg-navy-800 dark:shadow-none md:py-[30px]`}
                                                onClick={() => {
                                                    action(actionParam);
                                                }}
                                            >
                                                <img
                                                    src={src}
                                                    alt={alt}
                                                    className="h-14"
                                                />
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        }
                    />
                )}

                {user?.allow_new_user_requests && <Divider className="mt-3" />}

                <BasicLayout
                    title="Utilizza un tuo ordine"
                    subtitle="Seleziona un ordine precaricato dalla lista."
                    content={
                        <>
                            <ErrorMessage message={message} />

                            {resellerOrders.length ? (
                                <>
                                    <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(210px,1fr))]">
                                        {resellerOrders.map(
                                            ({
                                                code,
                                                reseller_code,
                                                type,
                                                subtype,
                                                notes,
                                            }) => (
                                                <div
                                                    key={code}
                                                    className="flex cursor-pointer flex-col gap-1.5 rounded-[8px] bg-white text-sm shadow-lg shadow-shadow-500 transition-all duration-300 hover:shadow-lg dark:!bg-navy-800 dark:shadow-none"
                                                    onClick={() => {
                                                        navigate(
                                                            `/my/reseller-order-detail/${code}`
                                                        );
                                                    }}
                                                >
                                                    <div className="flex w-full items-center justify-between rounded-tr-lg rounded-tl-lg border-b-2 border-gray-200 bg-[#F0F4F8] py-3 px-4">
                                                        <span className="font-semibold text-[#334155]">
                                                            Ordine
                                                        </span>

                                                        <div className="rounded-2xl border-2 border-gray-200 bg-white py-1 px-2">
                                                            {reseller_code}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col gap-3 py-2 px-4">
                                                        <div className="flex w-full items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <FontAwesomeIcon
                                                                    className="h-4 w-4 text-gray-700"
                                                                    icon={
                                                                        faPowerOff
                                                                    }
                                                                />

                                                                <span className="text-gray-700">
                                                                    Attivazione
                                                                </span>
                                                            </div>

                                                            <ActivationBadgeType
                                                                text={type}
                                                            />
                                                        </div>

                                                        <div className="flex w-full items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <FontAwesomeIcon
                                                                    className="h-4 w-4 text-gray-700"
                                                                    icon={
                                                                        faKeySkeleton
                                                                    }
                                                                />

                                                                <span className="text-gray-700">
                                                                    Licenza
                                                                </span>
                                                            </div>

                                                            <span>
                                                                {subtype}
                                                            </span>
                                                        </div>

                                                        {notes && (
                                                            <div className="flex w-full items-center justify-between gap-4">
                                                                <div className="flex items-center gap-3">
                                                                    <FontAwesomeIcon
                                                                        className="h-4 w-4 text-gray-700"
                                                                        icon={
                                                                            faPen
                                                                        }
                                                                    />

                                                                    <span className="text-gray-700">
                                                                        Note
                                                                    </span>
                                                                </div>

                                                                <span className="truncate">
                                                                    {notes}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>

                                    <Paginator
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={(page) =>
                                            setCurrentPage(page)
                                        }
                                    />
                                </>
                            ) : (
                                <div className="flex min-h-[5rem] w-full items-center justify-center text-[14.5px] text-gray-800">
                                    <span>
                                        Non è presente nessun ordine al momento
                                    </span>
                                </div>
                            )}
                        </>
                    }
                />
            </Card>
        </>
    );
};

export default NewActivation;
